/**
 * Pro Required Error Handler Hook
 *
 * Provides utilities for handling `requires_pro` errors from IPC calls.
 * When an IPC call returns a ProRequiredError, this hook provides a handler
 * that shows the UpgradeDialog with the relevant feature highlighted.
 *
 * @example
 * ```tsx
 * import { useProRequiredHandler } from '@/core/licensing';
 *
 * function MyComponent() {
 *   const { handleProRequired, handleIpcResult } = useProRequiredHandler();
 *
 *   const doSomething = async () => {
 *     const result = await window.electronAPI.someProFeature();
 *     // Option 1: Handle with callback pattern
 *     if (handleIpcResult(result)) {
 *       // result was not a Pro error, proceed normally
 *       console.log('Success:', result);
 *     }
 *     // Option 2: Check and handle manually
 *     if (isProRequiredError(result)) {
 *       handleProRequired(result);
 *       return;
 *     }
 *   };
 * }
 * ```
 */

import { useCallback } from 'react';
import { isProRequiredError, type ProRequiredError, type ProFeature } from '../types';
import { useProUpgradePromptStore } from '../store/proUpgradePromptStore';

export interface UseProRequiredHandlerReturn {
  /**
   * Show the upgrade dialog for a Pro-required error.
   * Call this when you receive a ProRequiredError from an IPC call.
   */
  handleProRequired: (error: ProRequiredError) => void;

  /**
   * Check if a result is a Pro-required error and handle it if so.
   * Returns true if the result is NOT a Pro error (operation can proceed).
   * Returns false if the result IS a Pro error (upgrade dialog shown).
   *
   * @example
   * ```tsx
   * const result = await window.electronAPI.someProFeature();
   * if (!handleIpcResult(result)) {
   *   return; // Pro error was handled, dialog shown
   * }
   * // Continue with normal handling
   * ```
   */
  handleIpcResult: <T>(result: T | ProRequiredError) => result is T;

  /**
   * Show the upgrade dialog for a specific feature.
   * Useful when you want to trigger the dialog without an IPC error.
   */
  showUpgradePrompt: (feature?: ProFeature) => void;
}

/**
 * Hook for handling Pro-required errors from IPC calls.
 * Provides utilities to detect and respond to `requires_pro` errors
 * by showing the UpgradeDialog with the appropriate feature highlighted.
 */
export function useProRequiredHandler(): UseProRequiredHandlerReturn {
  const showPrompt = useProUpgradePromptStore((s) => s.showPrompt);

  const handleProRequired = useCallback(
    (error: ProRequiredError) => {
      console.log(
        `[ProRequiredHandler] Pro feature required: ${error.featureName}`,
        error.featureDescription
      );
      showPrompt(error.feature);
    },
    [showPrompt]
  );

  const handleIpcResult = useCallback(
    <T>(result: T | ProRequiredError): result is T => {
      if (isProRequiredError(result)) {
        handleProRequired(result);
        return false;
      }
      return true;
    },
    [handleProRequired]
  );

  const showUpgradePrompt = useCallback(
    (feature?: ProFeature) => {
      showPrompt(feature);
    },
    [showPrompt]
  );

  return {
    handleProRequired,
    handleIpcResult,
    showUpgradePrompt,
  };
}
